import type { ICaaToken } from "../interface/customer-account";
import type { GraphQLFormattedError, ICaaClientConfig, TCaaRequestFn } from "../types/graphql";
import { CaaTokenRejectedError, refreshCaaToken } from "../utils/caaAuth";
import { discoverCaaEndpoints } from "../utils/caaDiscovery";

// access_token 만료 60초 전부터는 미리 갱신
const EXPIRY_BUFFER_MS = 60_000;

/**
 * Customer Account API 클라이언트를 만듦
 * createStorefrontRequest(@shopify/storefront-api-client, 공개 토큰)와 달리 CAA는 endpoint가
 * discovery로 얻어지고 인증도 OAuth access token을 Authorization 헤더에 직접 실어야 해서 fetch를 그대로 사용
 *
 * - request: 매 요청마다 endpoint discovery + 토큰 만료 검사(60초 버퍼) 후 갱신하는 GraphQL 실행기
 * - getFreshToken: request 바깥에서 유효한 access token이 필요한 곳(Storefront API의 buyerIdentity 등)에서 사용
 */
export function createCaaClient<TToken extends ICaaToken = ICaaToken>(config: ICaaClientConfig<TToken>) {
  // 동시에 여러 요청이 만료 임박을 감지해도 refresh 호출은 한 번만 나가도록 in-flight 요청을 공유
  let inFlightRefresh: Promise<ICaaToken | null> | null = null;

  const refreshWithRefreshToken = (refreshToken: string) =>
    refreshCaaToken({ shopDomain: config.shopDomain, clientId: config.clientId, refreshToken });

  const refresh = (token: TToken): Promise<ICaaToken | null> => {
    if (inFlightRefresh) return inFlightRefresh;

    inFlightRefresh = (async () => {
      const next = config.refresh
        ? await config.refresh(token, refreshWithRefreshToken)
        : await refreshWithRefreshToken(token.refreshToken);

      if (!next) {
        config.onRefreshFailed();
        return null;
      }

      config.setToken(next);
      return next;
    })()
      .catch((error: unknown) => {
        // refresh_token 만료/폐기 등으로 갱신이 거부되면 store 정리. 일시적인 네트워크 오류는 토큰이 아직 유효할 수 있어 그대로 던짐
        if (error instanceof CaaTokenRejectedError) config.onRefreshFailed();
        throw error;
      })
      .finally(() => {
        inFlightRefresh = null;
      });

    return inFlightRefresh;
  };

  /** 만료 임박(60초 이내)이면 갱신 후 반환, 아니면 현재 토큰을 그대로 반환. 로그인 안 된 상태면 null */
  const getFreshToken = async (): Promise<ICaaToken | null> => {
    const token = config.getToken();

    if (token && new Date(token.expiresAt).getTime() - Date.now() < EXPIRY_BUFFER_MS) {
      return refresh(token);
    }

    return token;
  };

  const request: TCaaRequestFn = async (document, variables) => {
    const [{ graphqlApiEndpoint }, token] = await Promise.all([discoverCaaEndpoints(config.shopDomain), getFreshToken()]);

    const response = await fetch(graphqlApiEndpoint, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token?.accessToken ? { Authorization: token.accessToken } : {}),
      },
      body: JSON.stringify({ query: document, variables }),
    });

    if (!response.ok) {
      const error = new Error(`[CAA] Network error : ${response.status}`);
      config.onNetworkError?.(error, { status: response.status });
      throw error;
    }

    const { data, errors } = (await response.json()) as { data?: unknown; errors?: GraphQLFormattedError[] };

    if (errors?.length) {
      config.onGraphQLErrors?.(errors);
      throw new Error(`[CAA] GraphQL error : ${errors.map((error) => error.message).join(", ")}`);
    }

    return data as never;
  };

  return { request, getFreshToken };
}
